import { Component } from '@angular/core';
import { HttpHeaders, HttpClient, HttpResponse } from '@angular/common/http';
import { Subscription, timer } from 'rxjs';


import { DataLayerWMSComponent } from './data-layer-wms.component';
import { MapService } from './map.service';
import { DataLayerService } from './data-layer.service';
import { ConfigService } from './config.service';
import { UserMessageService } from './user-message.service';

declare var L;

/*
 * data-layer-wms-time defines a class to display time-enabled wms data.
 *
 * Within the configuration file, WMS-T radar layers can be included:
 * "overlayLayers": [
 * 		{"id": "radarLayerNWSLooping", "label": "NWS Radar (loop)",
 * 			"url": "https://hazards.fema.gov/gis/nfhl/services/public/NFHLWMS/MapServer/WMSServer",
 *			"class": "data-layer-wms",
 * 			"legendUrl": "https://hazards.fema.gov/gis/nfhl/services/public/NFHLWMS/MapServer/WmsServer?request=GetLegendGraphic&version=1.3.0&format=image/png&layer=4", "refresh": true },
 * ...
 * ]
			<div [ngClass]={doNotCreate:!active} style="padding-left: 25px"><img src={{legendUrl}} width="200"></div>
 */

/*
 * Observations:
	NWS seems to cut off after it's been running for a while (10+ minutes?). 
	I think there is some query limitation on their end, but I'm not sure if there's anything we can do.

	Nexrad doesn't seem to have that limitation, but it seems to be slower to load.
	Both animations will have problems where only some tiles will load at first,
	it takes a few loops for them all to show up. Nexrad seems to be worse about that.
*/

@Component({
	selector: 'data-layer-wms-time',
	standalone: false,
	template: `
		<div class="checkboxDiv">
			<label class="overlay-layer" title={{titleString}}>
				<input type="checkbox" (click)="menuControlSelected($event)">{{htmlElementLabel}}
			</label>
			<app-single-legend [active]=active [legendUrl]=legendUrl></app-single-legend>
		</div>
		`,
	styleUrls: ['app.component.css']
})
export class DataLayerWMSTimeComponent extends DataLayerWMSComponent {
	
	align = false; // Whether to align the requested times to 5 minute intervals (that is, 6:31 -> 6:30). Necessary for Nexrad.
	cycleStartTime = 0; // The time to end the animation at.
	interval = 10; // How much to go back for each frame, in minutes
	lastLayer; // holds the previous radar image so it can be removed on looping
	layerArray = []; // Caches layers to reduce the number of website queries
	maxTime = 90; // Maximum amount of time to look back in the radar, in minutes
	opacity = 0.4; // Default opacity of the layer - can be edited in the config.
	subscription: Subscription; // Used with the timer to get a new animation frame every 1/2 second
	timeIndex = 0; // The current frame of the animation.
	timer; // Used to get a new animation frame every 1/2 second.
	titleString = ""; // Label for the sidebar element.
	waitIndex = 0.0; // How much time has elapsed between animations, in seconds.
	waitTime = 3; // The amount of time to wait between animations, in seconds.

	constructor(
		mapService: MapService,
		dataLayerService: DataLayerService,
		configService: ConfigService,
		userMessageService: UserMessageService,
		http: HttpClient) {
		super(mapService, dataLayerService, configService, userMessageService, http);
	
		this.timer = timer(2000, 500);
		this.subscription = this.timer.subscribe( t => {
			this.displayRadar();
		});

		this.titleString='Images ' + this.interval + ' minutes apart. Activate and hover for more detailed information.';
	}

	menuControlSelected (event): void {
		if ( event.target.checked ) {
			this.activateLayer();
		} else {
			this.deactivateLayer();
		}

	}

	setData ( someData: any ) {
        super.setData(someData);

		if (someData.hasOwnProperty('align')) {
			this.align = someData.align;
		}

		if (someData.hasOwnProperty('interval')) {
			this.interval = someData.interval;
		}

		if (someData.hasOwnProperty('maxTime')) {
			this.maxTime = someData.maxTime;
		}
        
		if (someData.hasOwnProperty('opacity')) {
			this.opacity = someData.opacity;
		}

		if (someData.hasOwnProperty('waitTime')) {
			this.waitTime = someData.waitTime;
		}
	}

	// refreshLayer first checks to see if the layer is active.  If so, activateLayer is called.
	refreshLayer() {
		if ( this.active === true ) {
			this.activateLayer();
		}
	}

	// First check whether the information has been read.
	// Display the markers on the map and set the layer to active.
	activateLayer () {
		console.log('Activating ' + this.id + ' layer.');
		if ( this.mapLayer != null ) {
			this.mapService.map.removeLayer(this.mapLayer);
		}
		if ( this.value == null || this.refresh === true ) {
			// once the data file has been read, drawMarkers is automatically called because an observer has been established
			this.parseDataFile();
		}

		// Resetting any data that might be left from previous animations.
		if ( this.layerArray.length > 0) {
			this.layerArray = [];
		}
		if ( this.lastLayer != null ) {
			this.mapService.map.removeLayer(this.lastLayer);
			this.lastLayer = null;
		}

		// Force an initial first call of displayRadar() to get immediate
		// changes.
		this.displayRadar();

		// activate legend as well
		const legendDisplay = this.mapService.getDataLayerLegendDisplay(this.id);
		if ( legendDisplay != null ) {
			legendDisplay['source'].next (true);
		}

		this.active = true;

	}

	deactivateLayer() {
		console.log('Deactivating ' + this.id + ' layer.');
		if ( this.active === true ) {
			if (this.mapLayer != null) {
				this.mapService.map.removeLayer(this.mapLayer);
				this.mapLayer = null;
			}
			if (this.lastLayer != null) {
				this.mapService.map.removeLayer(this.lastLayer);
				this.lastLayer = null;
			}
			this.timeIndex = 0;
			this.waitIndex = 0;

			this.active = false;
		}
		const legendDisplay = this.mapService.getDataLayerLegendDisplay(this.id);
		if ( legendDisplay != null ) {
			legendDisplay['source'].next (false);
		}
		this.titleString = 'Images ' + this.interval + ' minutes apart. Activate and hover for more detailed information.';
	}

	displayRadar() {
		if (this.active === false) {
			return;
		}

		// looping
		let maxImages = Math.floor(this.maxTime/this.interval);

		if (this.timeIndex == 0) {			
			if (this.mapLayer != null) {
				this.mapService.map.removeLayer(this.mapLayer);
				this.mapLayer = null;
			}
			if (this.lastLayer != null) {
				this.mapService.map.removeLayer(this.lastLayer);
				this.lastLayer = null;
			}

			if (this.waitIndex < this.waitTime) {
				this.waitIndex += 0.5;
				return;
			}
			// Only change reference time every 5 minutes
			// This allows for caching
			// The layer is only updated every 5 minutes, so
			// there's no data loss here.
			if (Date.now() > (this.cycleStartTime + 5*60*1000)) {
				if (this.align) {
					// If the align parameter is set, round to the nearest
					// 5 minute interval in the past.
					let date = Date.now();
					let remainder = date % (5*60*1000);
					this.cycleStartTime = date - remainder;
				}
				else {
					this.cycleStartTime = Date.now();
				}
				
				this.layerArray = [];
				this.updateTitle(maxImages);
			}

			if (!this.titleString.includes('from')) {
				// Still the default note, replace with useful one.
				this.updateTitle(maxImages);
			}
		}

		let prevLayer;
		prevLayer = this.mapLayer;

		// Subtract the index from the maximum number of images
		// because we should start with the oldest image and
		// go to the newest image.
		let fetchDate = new Date(this.cycleStartTime - ((maxImages - this.timeIndex) * (this.interval * 1000 * 60)));
		//let fetchDate = new Date(this.cycleStartTime - (this.timeIndex * this.interval * 1000 * 60));

		console.log("Fetching data at time: " + fetchDate.toISOString());

		if (this.layerArray.length == this.timeIndex ) {
			let mapLayer = L.tileLayer.wms( this.value, 
				{
					layers: this.layers, 
					transparent: this.transparent, 
					version: this.version, 
					format: this.format,
					time: fetchDate.toISOString(),
					opacity: this.opacity
				}
			);
			this.layerArray.push(mapLayer);
			this.mapLayer = mapLayer;
		}
		else {
			console.log('using cached layer');
			this.mapLayer = this.layerArray[this.timeIndex];
			if (!this.mapLayer) {
				let mapLayer = L.tileLayer.wms( this.value, 
					{
						layers: this.layers, 
						transparent: this.transparent, 
						version: this.version, 
						format: this.format,
						time: fetchDate.toISOString(),
						opacity: this.opacity
					}
				);
				this.layerArray[this.timeIndex] = mapLayer;
				this.mapLayer = mapLayer;
			}
		}
		
		this.mapLayer.addTo(this.mapService.map).bringToFront();

		// 	https://mapservices.weather.noaa.gov/eventdriven/services/radar/radar_base_reflectivity_time/ImageServer/WMSServer?service=WMS&request=GetMap&layers=radar_base_reflectivity_time&styles=&format=image/png&transparent=true&version=1.3.0&width=256&height=256&crs=EPSG:3857&bbox=-11545048.752193023,4852834.051769271,-11505912.993711013,4891969.810251283

		if (this.lastLayer != null) {
			this.mapService.map.removeLayer(this.lastLayer);
		}
	
		this.lastLayer = prevLayer;

		if (maxImages > this.timeIndex) {
			this.timeIndex += 1;
		}
		else {
			this.timeIndex = 0;
			this.waitIndex = 0;
		}
	}

	parseDataFile () {
		console.log('In data-layer-wms-time.parseDataFile.');
		if ( this.url.startsWith('http')) {
			this.value = this.url;
		} else {
			this.value = this.configService.baseDir + this.url + '?nocache=' + (new Date()).getTime();
		}
	}

	updateTitle(maxImages) {
		let startDate = new Date(this.cycleStartTime -(maxImages * this.interval * 1000*60));
		let endDate = new Date(this.cycleStartTime);
		this.titleString = "Images from " + startDate.toLocaleString() + " to " + endDate.toLocaleString();
	}

	// TODO emahon 2026-02-22: Does it make sense to do any error
	// handling if layers don't load right?
}
